import type { ReactNode } from "react";
import { InfoSidebar, InfoTicketList, LinkButtons, STATUS_LABEL, formatCountry, formatDate, formatLanguage, type InfoRow, type Navigate } from "@omnabase/ui";
import { fillCustomerLink, type CustomerLink, type Ticket } from "@omnabase/shared";
import { viewForTicket } from "../../lib/inbox";
import { SiteAddresses, SiteData } from "./TicketSite";
import { FormFacts, infoBlocks } from "./SubmissionBlock";
import { CUSTOMER_HISTORY_LIMIT, type useTicketSide } from "./ticketSide";

/** The customer column beside a ticket (moved out of the ticket page in the customer sidebar round, 2026-11-01): Profile (the tab read
 *  "Profile details" until the discussion polish round, 2026-11-02) and the customer's other tickets. The round's rules, in plain words:
 *  - Country and Language show only when set, by name ("AT" reads "Austria", "de" "German"); the Licenses row always stays ("None" is a
 *    reason to add one).
 *  - "Previous tickets" counts the other tickets the Tickets tab lists and opens that tab.
 *  - The project's customer links sit under the profile rows as small buttons, filled with this customer's details.
 *  - Moving between the customer's tickets from the Tickets tab keeps the tab (and the phone's drawer) open: the links carry `?tab=tickets`
 *    and `?info=1`.
 *  The ticket discussion round (2026-11-02): a third tab, Discussion (`?tab=discussion`), the team's internal chat about the ticket, with the
 *  reader's unread count; left out for a ticket in Deleted or Spam (the page passes none). The three tabs fit the 340 px band whole,
 *  also beside the drawer's close button (the flow measures it). */
type Side = ReturnType<typeof useTicketSide>;
export type SideTab = "profile" | "tickets" | "discussion";
/** the side panel's tab from the address: Tickets and Discussion by `?tab=`, else Profile */
export const sideTabOf = (param: string | null): SideTab => (param === "tickets" || param === "discussion" ? param : "profile");

export function CustomerSide({ ticket, side, links, tab, open, onTab, onClose, nav, ws, discussion }: {
  ticket: Ticket; side: Side; /** the ticket's project's customer links */ links: readonly CustomerLink[]; tab: SideTab; open: boolean;
  onTab: (tab: SideTab) => void; onClose: () => void; nav: Navigate; ws: (path: string) => string;
  /** the Discussion tab's content and its count; absent, two tabs */ discussion?: { content: ReactNode; count: number };
}) {
  const { customer, fromSystem, history } = side;
  const rows = profileRows(ticket, side, () => onTab("tickets"));
  const extra = <><CustomerLinkButtons links={links} who={{ email: ticket.customer.email, name: ticket.customer.name, id: ticket.customer.id }} /><SiteAddresses addresses={customer.data?.addresses} /><SiteData facts={fromSystem.data} /><FormFacts submission={ticket.submission} /></>;
  const keep = ticketLinkQuery(open);
  return (
    <InfoSidebar tab={discussion || tab !== "discussion" ? tab : "profile"} onTab={(t) => onTab(sideTabOf(t))} open={open} onClose={onClose} extra={extra} blocks={infoBlocks(fromSystem.data)} rows={rows}
      discussion={discussion}>
      <InfoTicketList onNavigate={nav} tickets={(history.data?.rows ?? []).map((t) => ({ id: t.id, href: `${ws(`/inbox/${viewForTicket(t)}/${t.number}`)}${keep}`, number: t.number, subject: t.subject, status: STATUS_LABEL[t.status], updatedAt: t.updatedAt, current: t.id === ticket.id }))} />
    </InfoSidebar>
  );
}

/** a ticket link from the Tickets tab keeps that tab, and the drawer when it is open */
export const ticketLinkQuery = (drawerOpen: boolean): string => `?${new URLSearchParams({ tab: "tickets", ...(drawerOpen ? { info: "1" } : {}) }).toString()}`;

/** the Profile tab's rows; the customer's own read wins over the ticket's copy (a write-through may have filled a row since) */
function profileRows(ticket: Ticket, side: Side, openTickets: () => void): InfoRow[] {
  const who = side.customer.data ?? ticket.customer;
  const licenses = side.customer.data?.licenses ?? [];
  const country = formatCountry(who.country), language = formatLanguage(who.language);
  return [
    { label: "Email", value: <a href={`mailto:${ticket.customer.email}`}>{ticket.customer.email}</a> },
    ...(who.company ? [{ label: "Company", value: who.company }] : []),
    { label: "Licenses", value: licenses.length ? `${licenses.length} · ${licenses.some((l) => l.verified) ? "verified" : "claimed, not verified"}` : "None" },
    ...(side.customer.data ? [{ label: "Customer since", value: formatDate(side.customer.data.customerSince) }] : []),
    ...(ticket.urgent ? [{ label: "Urgent", value: "Yes · first in the queue" }] : []),
    ...(country ? [{ label: "Country", value: country }] : []),
    ...(language ? [{ label: "Language", value: language }] : []),
    { label: "Previous tickets", value: previousTickets(ticket, side, openTickets) },
  ];
}

/** the other tickets the Tickets tab lists, as a button that opens it ("19+" when the list holds the 20 newest and there are more) */
function previousTickets(ticket: Ticket, side: Side, openTickets: () => void): ReactNode {
  const listed = side.history.data?.rows;
  if (!listed) return "…";
  const others = listed.filter((t) => t.id !== ticket.id).length;
  const more = listed.length >= CUSTOMER_HISTORY_LIMIT && !!side.history.data?.nextCursor;
  return <button type="button" className="ob-info__open-tickets" onClick={openTickets}>{`${others}${more ? "+" : ""}`}</button>;
}

/** the project's customer links as buttons, filled with the customer's email, name and id (percent-encoded) */
export function CustomerLinkButtons({ links, who, label }: { links: readonly CustomerLink[]; who: { email: string; name?: string | null; id: string }; label?: string }) {
  return <LinkButtons links={links.map((l) => ({ label: l.label, href: fillCustomerLink(l.url, who) }))} label={label} />;
}
